import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileText, ArrowLeftRight, Beef, Sprout, Boxes,
  Users, Wallet, LogOut, Menu, Shield, ShoppingCart, Package, FileStack, ClipboardList,
  BarChart3, ReceiptText, Truck, Globe, Contact,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { canAccess, ROLE_LABELS } from "../lib/permissions";
import { Logo } from "./Logo";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, module: "dashboard" },
  { to: "/reports", label: "Reports", icon: BarChart3, module: "reports" },
  { to: "/sales", label: "Sales & Receipts", icon: ShoppingCart, module: "sales" },
  { to: "/orders", label: "Online Orders", icon: ClipboardList, module: "orders" },
  { to: "/products", label: "Products", icon: Package, module: "products" },
  { to: "/invoices", label: "Invoices", icon: FileText, module: "invoices" },
  { to: "/transactions", label: "Income & Expenses", icon: ArrowLeftRight, module: "transactions" },
  { to: "/bills", label: "Bills", icon: ReceiptText, module: "bills" },
  { to: "/vendors", label: "Vendors", icon: Truck, module: "vendors" },
  { to: "/customers", label: "Customers", icon: Contact, module: "customers" },
  { to: "/documents", label: "Documents", icon: FileStack, module: "documents" },
  { to: "/livestock", label: "Livestock", icon: Beef, module: "livestock" },
  { to: "/crops", label: "Crops", icon: Sprout, module: "crops" },
  { to: "/inventory", label: "Inventory", icon: Boxes, module: "inventory" },
  { to: "/employees", label: "Employees", icon: Users, module: "employees" },
  { to: "/website", label: "Website", icon: Globe, module: "content" },
  { to: "/payroll", label: "Payroll", icon: Wallet, module: "payroll" },
  { to: "/admin", label: "User Management", icon: Shield, module: "admin" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = React.useState(false);

  const SidebarInner = () => (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2.5 px-6 py-6">
        <Logo size={40} className="ring-1 ring-white/20" />
        <div>
          <p className="font-display text-base font-extrabold tracking-tight text-white">Modav Farms</p>
          <p className="text-[11px] text-white/60">Internal Management</p>
        </div>
      </div>
      <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto px-3 py-2">
        {NAV.filter((n) => canAccess(user?.role, n.module)).map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={() => setOpen(false)}
            data-testid={`nav-${to.slice(1)}`}
            className={({ isActive }) =>
              `nav-link flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium ${
                isActive
                  ? "bg-white/10 text-white"
                  : "text-white/75 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <Icon className="h-[18px] w-[18px]" />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 px-4 py-4">
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            <AvatarImage src={user?.picture} />
            <AvatarFallback className="bg-primary text-white text-xs">
              {(user?.name || "U").slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-white">{user?.name}</p>
            <p className="truncate text-[11px] text-white/60">{ROLE_LABELS[user?.role] || user?.role}</p>
          </div>
          <button data-testid="logout-btn" onClick={logout} className="text-white/60 hover:text-white">
            <LogOut className="h-[18px] w-[18px]" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="sidebar-bg hidden w-64 shrink-0 lg:block">
        <SidebarInner />
      </aside>

      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="sidebar-bg absolute left-0 top-0 h-full w-64">
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-white/80 px-4 backdrop-blur-md sm:px-6 lg:hidden">
          <Button variant="ghost" size="icon" onClick={() => setOpen(true)} data-testid="menu-btn">
            <Menu className="h-5 w-5" />
          </Button>
          <Logo size={28} />
          <span className="font-display font-bold">Modav Farms</span>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
